import {
  AccessoryCheckCreatedInAppDataDto,
  BaseInAppNotificationDataDto,
  CallToActionInAppDataDto,
  DamageCostEvaluationAggregateUpdatedInAppDataDto,
  DamageCostEvaluationCompletedInAppDataDto,
  DamageCostEvaluationStartedInAppDataDto,
  DamageDetectionCreatedInAppDataDto,
  InAppNotificationReceivedDto,
  InAppNotificationType,
  NotificationDto,
  UserTaggedInEntityInAppDataDto,
} from "@/core/api/generated/v1";

import { ROUTE_PATH } from "../constants/routing";
import { TypedEventEmitter } from "../eventEmmiters/typedEventEmitter";
import { assertNever } from "../ts/typeHelpers";

export interface InAppNotificationAction {
  hasAction: boolean;
  routeUrl?: string;
  executeAction?: (notification: NotificationDto) => void;
}

export class NotificationService extends TypedEventEmitter<{
  // list of supported events
  inAppNotificationReceived: InAppNotificationReceivedDto;
}> {
  public handleNewInAppNotification(data: InAppNotificationReceivedDto) {
    this.emit("inAppNotificationReceived", data);
  }

  /** Returns an action according to InApp notification data. */
  public getInAppNotificationAction(notification: NotificationDto): InAppNotificationAction {
    const result: InAppNotificationAction = {
      hasAction: false,
      routeUrl: undefined,
      executeAction: undefined,
    };

    const baseData = notification.inApp?.data as BaseInAppNotificationDataDto | undefined;
    if (!notification.inApp || !baseData || !baseData.type) {
      return result;
    }

    switch (baseData.type) {
      case InAppNotificationType.None: {
        break;
      }
      case InAppNotificationType.CallToAction: {
        const data = baseData as CallToActionInAppDataDto;
        result.routeUrl =
          data.ctaEntityType && data.ctaEntityId
            ? ROUTE_PATH.ENTITY_VIEW_RESOLVER({
                entityType: data.ctaEntityType,
                entityId: data.ctaEntityId,
              })
            : undefined;
        break;
      }
      case InAppNotificationType.DamageDetectionCreated: {
        const data = baseData as DamageDetectionCreatedInAppDataDto;
        result.routeUrl = ROUTE_PATH.DAMAGE_DETECTION_VIEW(data.damageDetectionId);
        break;
      }
      case InAppNotificationType.DamageCostEvaluationStarted: {
        const data = baseData as DamageCostEvaluationStartedInAppDataDto;
        result.routeUrl = ROUTE_PATH.DAMAGE_COST_EVALUATION_VIEW(data.damageCostEvaluationId);
        break;
      }
      case InAppNotificationType.DamageCostEvaluationCompleted: {
        const data = baseData as DamageCostEvaluationCompletedInAppDataDto;
        result.routeUrl = ROUTE_PATH.DAMAGE_COST_EVALUATION_VIEW(data.damageCostEvaluationId);
        break;
      }
      case InAppNotificationType.DamageCostEvaluationAggregateUpdated: {
        const data = baseData as DamageCostEvaluationAggregateUpdatedInAppDataDto;
        result.routeUrl = ROUTE_PATH.DAMAGE_COST_EVALUATION_AGGREGATE_VIEW(
          data.damageCostEvaluationAggregateId,
        );
        break;
      }
      case InAppNotificationType.AccessoryCheckCreated: {
        const data = baseData as AccessoryCheckCreatedInAppDataDto;
        result.routeUrl = ROUTE_PATH.ACCESSORY_CHECK_VIEW(data.accessoryCheckId);
        break;
      }
      case InAppNotificationType.ChatResolved:
      case InAppNotificationType.ChatReopened:
      case InAppNotificationType.ChatAcknowledged:
      case InAppNotificationType.ChatParticipantAdded:
      case InAppNotificationType.ChatParticipantRemoved:
      case InAppNotificationType.ChatParticipantMentioned:
      case InAppNotificationType.ChatMessageAcknowledged:
      case InAppNotificationType.ChatNegotiationProposalCreated:
      case InAppNotificationType.ChatNegotiationProposalResponseGiven: {
        const data = baseData as { chatId?: string; chat?: { id?: string } };
        result.routeUrl = ROUTE_PATH.CHAT_VIEW(data.chatId || data.chat?.id);

        // const data = baseData as ChatResolvedInAppDataDto;
        // result.executeAction = () => {
        //   store.dispatch(
        //     chatsSlice.openChat({
        //       placement: "stack",
        //       chatType: data.chatType!,
        //       chatId: data.chatId,
        //       scope: undefined,
        //     }) as unknown as AnyAction,
        //   );
        // };
        break;
      }
      case InAppNotificationType.UserTaggedInEntity: {
        const data = baseData as UserTaggedInEntityInAppDataDto;
        if (data.entityType && data.entityId) {
          result.routeUrl = ROUTE_PATH.ENTITY_VIEW_RESOLVER({
            entityType: data.entityType,
            entityId: data.entityId,
          });
        }
        break;
      }
      default:
        assertNever(baseData.type); // TS error if case missing
    }

    result.hasAction = !!result.routeUrl || !!result.executeAction;
    return result;
  }
}

export const notificationService = new NotificationService();
